import { slimWorld, type CompileContext, type TilesetRole, type WorldDocument } from "daedalus-core";
import type { SlimNote } from "./slim-upgrade.ts";
import type { NamedFrame } from "./shrine-pair.ts";
import { markStartingShrines, type ShrineStartReport } from "./shrine-start.ts";
import { shrineMarkers, type ShrineReport } from "./shrine-upgrade.ts";
import { upgradeWells, type WellReport } from "./well-upgrade.ts";

/**
 * The upgrade every reader of a document applies in memory (ADR-042's amendment of 2026-10-03, the lead's review;
 * ADR-096 decision 43): the compile, so a world saved in an earlier shape still compiles and publishes; the editor,
 * as it opens a world, so the first save writes the new shape; and `world:migrate`, which writes it. Each step is a
 * pure function of the game and returns the same document when it finds nothing to carry over.
 */

/**
 * Upgrades a document from what a compile context holds: a two-state well to three states, then a shrine marker to a
 * behaviour on its obelisk, or on one placed where it stood, then the starting shrine marked on a map that has shrines and
 * none marked (ADR-096's amendment of 2026-10-05). Nothing is written.
 *
 * @param world The document.
 * @param context The world's compile context.
 * @param frames The catalogue's prop frames with their names, which pairing a shrine with its obelisk reads; every
 *   reader hands them over. With an empty list a marker can be neither paired nor placed and is left as it is.
 * @param roleOf The host's role of a tileset id (the catalogue's), which the slim upgrade needs (ADR-130 decision
 *   1.4, P1.6-T264); without it the tileset list is left as it is.
 * @returns The document, the same object when nothing was upgraded, one report per upgraded well, one per carried
 *   shrine marker, one per shrine marked the starting one, and what the slim upgrade removed and reduced.
 */
export function upgradeWorld(world: WorldDocument, context: Pick<CompileContext, "transitionStrips" | "frameSizes" | "collision">, frames: readonly NamedFrame[], roleOf?: (tileset: string) => TilesetRole | undefined): { readonly world: WorldDocument; readonly wells: readonly WellReport[]; readonly shrines: readonly ShrineReport[]; readonly starts: readonly ShrineStartReport[]; readonly slim: SlimNote } {
  const wells = upgradeWells(world, context);
  const shrines = shrineMarkers(wells.world, context, frames);
  const starts = markStartingShrines(shrines.world, context);
  const slim = roleOf === undefined ? { world: starts.world, removed: [], slimmed: [] } : slimWorld(starts.world, roleOf);
  return { world: slim.world, wells: wells.wells, shrines: shrines.shrines, starts: starts.starts, slim: { removed: slim.removed, slimmed: slim.slimmed } };
}
